// hardcoded-ui-text: allow — a developer-only panel: it returns null unless whatsappMediaService.isDevelopmentMode().
// It is not part of the product a customer or an operator sees, so its copy stays in
// English. Checked at the call site, not assumed from the folder name.
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Trash2, Activity, AlertTriangle } from 'lucide-react';
import { whatsappMediaService } from '@/services/whatsappMediaService';
import { useToast } from '@/hooks/use-toast';

export function MediaDebugPanel() {
  const [cacheStatus, setCacheStatus] = useState<Record<string, unknown> | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { toast } = useToast();

  const refreshCacheStatus = () => {
    const status = whatsappMediaService.getCacheStatus();
    setCacheStatus(status);
  };

  useEffect(() => {
    if (isVisible) {
      refreshCacheStatus();
      const interval = setInterval(refreshCacheStatus, 2000);
      return () => clearInterval(interval);
    }
  }, [isVisible]);

  const handleClearCache = () => {
    whatsappMediaService.clearCache();
    refreshCacheStatus();
    toast({
      title: "Cache cleared",
      description: "Media cache has been cleared. Images will reload.",
    });
  };

  const handleForceRefresh = () => {
    window.location.reload();
  };

  if (!whatsappMediaService.isDevelopmentMode()) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {!isVisible ? (
        <Button
          onClick={() => setIsVisible(true)}
          variant="outline"
          size="sm"
          className="bg-background/80 backdrop-blur-sm"
        >
          <Activity className="h-4 w-4" />
        </Button>
      ) : (
        <Card className="w-80 bg-background/95 backdrop-blur-sm border-border/50">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Media Debug Panel
              </CardTitle>
              <Button
                onClick={() => setIsVisible(false)}
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
              >
                ×
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {cacheStatus && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span>Cache Entries:</span>
                  <Badge variant="secondary">{cacheStatus.cacheSize}</Badge>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span>Pending Downloads:</span>
                  <Badge variant={cacheStatus.pendingDownloads > 0 ? "default" : "secondary"}>
                    {cacheStatus.pendingDownloads}
                  </Badge>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Button
                onClick={handleClearCache}
                variant="outline"
                size="sm"
                className="w-full"
              >
                <Trash2 className="h-3 w-3 mr-2" />
                Clear Cache
              </Button>
              
              <Button
                onClick={handleForceRefresh}
                variant="outline"
                size="sm"
                className="w-full"
              >
                <RefreshCw className="h-3 w-3 mr-2" />
                Force Refresh
              </Button>
            </div>

            <div className="text-xs text-muted-foreground bg-muted/30 p-2 rounded border-l-2 border-destructive/20">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-3 w-3 mt-0.5 text-destructive/60" />
                <div>
                  <div className="font-medium">Development Mode</div>
                  <div>If images disappear, use "Clear Cache" or "Force Refresh"</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}